import type { AirdropMerkleData, V4Config } from "@repo/shared-types";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";

import { generateMerkleTree, validateAirdropSupply } from "../../lib/airdrop-merkle";
import { fadeInUp } from "../../lib/animations";
import { Button } from "../ui/Button";
import { FeeSplitSliders } from "./FeeSplitSliders";
import { V4AdvancedConfig } from "./V4AdvancedConfig";

type V4ConfigStepProps = {
	data: Partial<V4Config>;
	onDataChange: (data: Partial<V4Config>) => void;
	onNext: () => void;
	onBack: () => void;
	onAirdropMerkleData?: (data: AirdropMerkleData, treeDump: unknown) => void;
};

export function V4ConfigStep({
	data,
	onDataChange,
	onNext,
	onBack,
	onAirdropMerkleData,
}: V4ConfigStepProps) {
	const [isAdvancedMode, setIsAdvancedMode] = useState(false);
	const [isGeneratingMerkle, setIsGeneratingMerkle] = useState(false);
	const [merkleError, setMerkleError] = useState<string | null>(null);
	const generatingRef = useRef(false);

	const updateData = <K extends keyof V4Config>(field: K, value: V4Config[K]) => {
		const partial: Partial<V4Config> = {};
		partial[field] = value;
		onDataChange(partial);
	};

	const isValid = () => {
		const feeSplitSum =
			(data.feeSplitCreator ?? 0) + (data.feeSplitTreasury ?? 0) + (data.feeSplitProtocol ?? 0);
		return feeSplitSum === 100;
	};

	const getAirdropExtension = () => {
		return data.extensions?.find((ext) => ext.type === "Airdrop");
	};

	const handleNext = async () => {
		if (!isValid()) return;
		if (generatingRef.current) return;

		const airdropExt = getAirdropExtension();
		if (airdropExt && airdropExt.type === "Airdrop") {
			if (airdropExt.recipients.length === 0) {
				setMerkleError("Airdrop requires at least one recipient. Upload an address list.");
				return;
			}
			// Validate supply range before generating Merkle tree
			const totalAmount = airdropExt.recipients.reduce((sum, r) => sum + r.amount, 0);
			const supplyError = validateAirdropSupply(totalAmount);
			if (supplyError) {
				setMerkleError(supplyError);
				return;
			}
			generatingRef.current = true;
			setIsGeneratingMerkle(true);
			setMerkleError(null);
			try {
				const result = await generateMerkleTree(airdropExt.recipients);
				onAirdropMerkleData?.(
					{
						merkleRoot: result.merkleRoot,
						amount: result.amount,
					},
					result.treeDump
				);
				onNext();
			} catch (e: unknown) {
				const message = e instanceof Error ? e.message : "Failed to generate Merkle tree";
				setMerkleError(message);
			} finally {
				setIsGeneratingMerkle(false);
				generatingRef.current = false;
			}
			return;
		}

		onNext();
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h2 className="text-2xl font-bold text-pastel-black mb-2">V4 Configuration</h2>
					<p className="text-pastel-gray">
						Configure your token's fee structure and Uniswap V4 features.
					</p>
				</div>

				{/* Simple/Advanced Toggle */}
				<div className="flex items-center space-x-3">
					<span className={`text-sm ${!isAdvancedMode ? "text-pastel-black" : "text-pastel-gray"}`}>
						Simple
					</span>
					<button
						type="button"
						className={`
							relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
							${isAdvancedMode ? "bg-pastel-vibrant-blue" : "bg-pastel-light-gray"}
						`}
						onClick={() => setIsAdvancedMode(!isAdvancedMode)}
						aria-label={`Switch to ${isAdvancedMode ? "simple" : "advanced"} mode`}
					>
						<span
							className={`
								inline-block h-4 w-4 rounded-full bg-white shadow-lg transition-transform
								${isAdvancedMode ? "translate-x-6" : "translate-x-1"}
							`}
						/>
					</button>
					<span className={`text-sm ${isAdvancedMode ? "text-pastel-black" : "text-pastel-gray"}`}>
						Advanced
					</span>
				</div>
			</div>

			{/* Fee Split Explanation */}
			<div className="bg-pastel-light-gray/50 border border-pastel-border rounded-lg p-4">
				<p className="text-sm text-pastel-gray">
					<strong>Fee Split:</strong> Determines how trading fees are distributed between you
					(creator), the community treasury, and the protocol. All percentages must sum to 100%.
				</p>
			</div>

			{/* Fee Split Sliders (always visible) */}
			<FeeSplitSliders
				feeSplitCreator={data.feeSplitCreator ?? 50}
				feeSplitTreasury={data.feeSplitTreasury ?? 30}
				feeSplitProtocol={data.feeSplitProtocol ?? 20}
				onCreatorChange={(value) => updateData("feeSplitCreator", value)}
				onTreasuryChange={(value) => updateData("feeSplitTreasury", value)}
				onProtocolChange={(value) => updateData("feeSplitProtocol", value)}
			/>

			{/* Advanced Mode Panel */}
			<AnimatePresence>
				{isAdvancedMode && (
					<motion.div
						initial="hidden"
						animate="visible"
						exit="exit"
						variants={fadeInUp}
						className="border-t border-pastel-border pt-6"
					>
						<V4AdvancedConfig
							extensions={data.extensions ?? []}
							onExtensionsChange={(value) => updateData("extensions", value)}
						/>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Merkle Generation Status */}
			{isGeneratingMerkle && (
				<div className="flex items-center space-x-2 text-sm text-pastel-gray">
					<span className="animate-spin">⏳</span>
					<span>Generating Merkle tree...</span>
				</div>
			)}
			{merkleError && <p className="text-sm text-red-500">{merkleError}</p>}

			{/* Navigation */}
			<div className="flex justify-between pt-6">
				<Button variant="outline" onClick={onBack}>
					Back
				</Button>
				<Button onClick={() => void handleNext()} disabled={!isValid() || isGeneratingMerkle}>
					{isGeneratingMerkle ? "Generating..." : "Next Step"}
				</Button>
			</div>
		</div>
	);
}
